'use client'

import { toError } from '@moment/observability/logger'
import { useEffect, useState } from 'react'

import { useDeleteProposalAllocationMutation } from '@/legacy-api/proposals-slice'
import { createLogger } from '@/lib/logger'

const logger = createLogger('ProposalAllocationTable')

interface UseQuantityInputProps {
  initialQuantity: number
  onUpdate: (newQuantity: number) => void
}

interface DeleteAllocationParams {
  proposalId: string
  proposalOrderId: string
  proposalAllocationId: string
}

// hook to manage qty input state and updates
export function useQuantityInput({
  initialQuantity,
  onUpdate,
}: UseQuantityInputProps) {
  // Initialize with the initialQuantity to prevent null
  const [inputQuantity, setInputQuantity] = useState<number>(initialQuantity)

  // sync input with initial qty when it changes
  useEffect(() => {
    setInputQuantity(initialQuantity)
  }, [initialQuantity])

  const handleQuantityChange = (value: string) => {
    // Remove non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '')

    // Handle decimal points - keep only the first one
    const [whole, ...decimals] = sanitizedValue.split('.')
    const cleanedValue =
      decimals.length > 0 ? `${whole}.${decimals.join('')}` : sanitizedValue

    // Parse the value and set it, or use initial quantity if invalid
    const parsedValue = cleanedValue ? parseFloat(cleanedValue) : NaN
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    setInputQuantity(isNaN(parsedValue) ? initialQuantity : parsedValue)
  }

  // only trigger update if value has changed and is valid
  const handleQuantityUpdate = () => {
    // Don't update if the input is NaN, zero, negative, or unchanged
    if (
      // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
      isNaN(inputQuantity) ||
      inputQuantity <= 0 ||
      inputQuantity === initialQuantity
    ) {
      // Reset to initial value if invalid
      setInputQuantity(initialQuantity)
      return
    }

    // At this point, inputQuantity is guaranteed to be a positive number
    onUpdate(inputQuantity)
  }

  return {
    inputQuantity,
    handleQuantityChange,
    handleQuantityUpdate,
  }
}

// hook to handle allocation deletion
export function useDeleteAllocation() {
  const [deleteAllocation] = useDeleteProposalAllocationMutation()

  const handleDelete = async ({
    proposalId,
    proposalOrderId,
    proposalAllocationId,
  }: DeleteAllocationParams) => {
    try {
      await deleteAllocation({
        proposal_id: proposalId,
        proposal_order_id: proposalOrderId,
        proposal_allocation_id: proposalAllocationId,
      })
    } catch (error) {
      logger.error('Failed to delete allocation:', {
        error: toError(error),
        proposalId,
        proposalOrderId,
        proposalAllocationId,
      })
    }
  }

  return handleDelete
}
